import React from "react";
import { Chip, Stack, Tooltip, Typography } from "@mui/material";

const tooltipPopperProps = {
  disablePortal: false,
  modifiers: [
    {
      name: "flip",
      options: {
        fallbackPlacements: ["bottom", "left", "right"],
      },
    },
    {
      name: "preventOverflow",
      options: {
        altAxis: true,
        padding: 8,
      },
    },
  ],
};

/**
 * Compact chip list for tables and cards.
 * Table: one line, ellipsis inside chips, +N always fully visible.
 * Drawer: pass sx={{ flexWrap: "wrap" }} with maxVisible={null}.
 */
const ChipList = ({
  items = [],
  maxVisible = 1,
  emptyLabel = "—",
  size = "small",
  variant = "outlined",
  color = "default",
  sx = {},
}) => {
  const list = (Array.isArray(items) ? items : [])
    .map((item) => (typeof item === "string" ? item : item?.name || item?.role || ""))
    .filter(Boolean);

  if (!list.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyLabel}
      </Typography>
    );
  }

  const wrapping = sx.flexWrap === "wrap" || sx.flexWrap === "wrap-reverse";
  const limit = wrapping || maxVisible == null || maxVisible < 0 ? list.length : maxVisible;
  const visible = list.slice(0, limit);
  const remainingItems = list.slice(limit);
  const remaining = remainingItems.length;

  return (
    <Stack
      direction="row"
      spacing={0.75}
      useFlexGap
      alignItems="center"
      sx={{
        width: "100%",
        minWidth: 0,
        overflow: wrapping ? "visible" : "hidden",
        ...sx,
      }}
    >
      {visible.map((item) => (
        <Tooltip
          key={item}
          title={item}
          arrow
          placement="top"
          PopperProps={tooltipPopperProps}
        >
          <Chip
            label={item}
            size={size}
            variant={variant}
            color={color}
            sx={{
              minWidth: 0,
              maxWidth: wrapping
                ? "100%"
                : remaining > 0
                  ? `calc((100% - 44px) / ${Math.max(visible.length, 1)})`
                  : visible.length > 1
                    ? `calc(100% / ${visible.length})`
                    : "100%",
              flexShrink: wrapping ? 0 : 1,
              "& .MuiChip-label": {
                overflow: wrapping ? "visible" : "hidden",
                textOverflow: wrapping ? "unset" : "ellipsis",
                whiteSpace: wrapping ? "normal" : "nowrap",
                display: "block",
              },
            }}
          />
        </Tooltip>
      ))}
      {remaining > 0 ? (
        <Tooltip
          title={remainingItems.join(", ")}
          arrow
          placement="top"
          PopperProps={tooltipPopperProps}
        >
          <Chip
            label={`+${remaining}`}
            size={size}
            color="primary"
            variant="outlined"
            sx={{ flexShrink: 0 }}
          />
        </Tooltip>
      ) : null}
    </Stack>
  );
};

export default ChipList;
